import React, { FC, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useStore } from '../../../store/store-provider';
import PollEntity from '../../../store/polls-store/poll-entity';
import { CREATE_POLL_BUTTON_TEXT, MAX_POLLS_LIST_SIZE, PollsTabsOnMobile } from '../../../constans/polls-constants';
import PollsContentManagerWrapper from './polls-content-manager-wrapper';
import ManagePoll from '../manage-poll/manage-poll';
import DraggablePollsList from '../draggable-polls-list/draggable-polls-list';
import { DropResult } from 'react-beautiful-dnd';
import { getNewPrioritize } from '../../../utils/helpers/prioritize-helpers';
import PollDetails from '../poll-details/poll-details';
import ContainerModalManager from './container-modal-manager';
import PollsMobileNavigation from '../polls-mobile-navigation/polls-mobile-navigation';
import { MIDDLE_SIZE_BREAKPOINT } from '../../../constans/responsive-break-points';
import { MIXPANEL_EVENT_IDENTIFIERS, MIXPANEL_EVENT_TYPES } from '../../../constans/mixpanel-contants';
import { getMixpanelService } from '../../../utils/services/mixpanel-service-instance';

const PollsContainer: FC = () => {
  const { pollsStore, podiumInfoStore } = useStore();
  const {
    getPollEntityById,
    pollsList,
    selectedPollId,
    getPollsList,
    onCreatePollSubscription,
    onUpdatePollSubscription,
    toggleManagePollModal,
    updatePoll,
    setShouldShowEllipsisMenu,
    isManagePollModalOpened,
    isEllipsisMenuOpened,
    getPollChoisesList,
    onDeletePollSubscription,
    onSavePollVotesSubscription,
    activeMobileTab,
    setActiveTab,
    isFullScreenModeEnabled,
    setShouldShowFullScreen,
    downloadPollsCSV,
    isOnePollOpened,
    getLiveInfoForCommand,
    getPollsTabConfig,
    setIsFullScreenModeSafari,
    isFullScreenModeSafari,
    setSelectedPollId,
  } = pollsStore;

  const copyPolls = [...pollsList] as PollEntity[];
  const isPollListLimitExceeded = copyPolls.length >= MAX_POLLS_LIST_SIZE;
  const selectedPoll = getPollEntityById(selectedPollId);
  const pollsContainerClassName = `polls-container ${copyPolls.length ? 'bg-white' : 'bg-grey'}`;
  const { getPresentationId, isResolutionBreakPointActive } = podiumInfoStore;
  const presentationId = getPresentationId();
  const isMobile = isResolutionBreakPointActive(MIDDLE_SIZE_BREAKPOINT);
  const mobileWrapperCN = isMobile ? activeMobileTab?.toLowerCase() + '-tab' : '';
  const shouldShowMobileDetails = !(isMobile && !copyPolls.length && !isManagePollModalOpened);
  const showMenuNavigation = isMobile && !!copyPolls.length;


  useEffect(() => {
    if (!presentationId) return;
    // Resolve the player type (isNextGenPlayer) before loading the polls, so a completed poll's
    // results button renders the correct variant (Show Results vs Add Results Slide) on first paint
    // instead of flipping once the async player-type lookup returns.
    const loadPolls = async (): Promise<void> => {
      await getLiveInfoForCommand();
      getPollsList();
      getPollsTabConfig();
    };
    loadPolls();

    const updatePollSubscription = onUpdatePollSubscription();
    const createPollSubscription = onCreatePollSubscription();
    const deletePollSubscription = onDeletePollSubscription();
    const savePollVotesSubscription = onSavePollVotesSubscription();

    return () => {
      createPollSubscription.unsubscribe();
      updatePollSubscription.unsubscribe();
      deletePollSubscription.unsubscribe();
      savePollVotesSubscription.unsubscribe();
    };
  }, []);

  const createPollHandler = (): void => {
    if (isPollListLimitExceeded) {
      pollsStore.shouldShowNotificationModal = true;
      return;
    }
    setActiveTab(PollsTabsOnMobile.DETAILS);
    setSelectedPollId('');
    toggleManagePollModal();
    getMixpanelService().track(MIXPANEL_EVENT_IDENTIFIERS.POLLS, MIXPANEL_EVENT_TYPES.CREATED);
  };

  const selectPoll = (pollId: string): void => {
    setSelectedPollId(pollId);
    const poll = getPollEntityById(pollId);
    if (isEllipsisMenuOpened) setShouldShowEllipsisMenu(false);
    if (poll && !poll.responses.length) getPollChoisesList(pollId);
  };

  const reorder = (startIndex: number, endIndex: number) => {
    if (startIndex === endIndex) return;
    const targetEntity = copyPolls[startIndex];
    const prioritize = getNewPrioritize({
      startIndex,
      endIndex,
      list: copyPolls
    });

    targetEntity.prioritizeIndex = Math.floor(prioritize);

    const updateBody = {
      order: targetEntity.prioritizeIndex,
      pollId: targetEntity.getId(),
    };

    updatePoll(updateBody);
  };

  const onDragEndHandler = (result: DropResult): void => {
    const { source, destination } = result;
    if (!destination) return;
    reorder(
      source.index,
      destination.index,
    );
  };

  const onDragStartHandler = (result: DropResult): void => {
    selectPoll(result.draggableId);
    setShouldShowEllipsisMenu(false);
  };

  const getContentWrapperChild = (): React.ReactElement | null => {
    if (isManagePollModalOpened) {
      return <ManagePoll poll={selectedPoll}/>;
    }

    return selectedPoll && !isManagePollModalOpened
      ? <PollDetails poll={selectedPoll}/>
      : null;
  };

  const downloadPollsCsvHandler = (): void => {
    downloadPollsCSV();
    getMixpanelService().track(MIXPANEL_EVENT_IDENTIFIERS.POLLS, MIXPANEL_EVENT_TYPES.DOWNLOAD);
  };

  return (
    <div className={`wrapper ${mobileWrapperCN}`}>
      <div className={pollsContainerClassName}>
        <div className='polls-title-list'>
          <h4>Polls ({copyPolls.length})</h4>
          <button className="polls-download" onClick={downloadPollsCsvHandler} disabled={!(copyPolls.length)}/>
          {!!copyPolls.length && (
            <span className='create-poll-button' onClick={createPollHandler}>
              {CREATE_POLL_BUTTON_TEXT}
            </span>
          )}
        </div>
        <DraggablePollsList
          list={copyPolls}
          selectPoll={selectPoll}
          onDragEndHandler={onDragEndHandler}
          onDragStartHandler={onDragStartHandler}
          toggleManagePollModal={toggleManagePollModal}
          isMobile={isMobile}
          isOnePollOpened={isOnePollOpened}
        />
      </div>

      {shouldShowMobileDetails &&
        <PollsContentManagerWrapper
          selectedPoll={selectedPoll}
          isListEmpty={!copyPolls.length}
          toggleManagePollModal={toggleManagePollModal}
          isFullScreenModeEnabled={isFullScreenModeEnabled}
          getPollChoisesList={getPollChoisesList}
          setShouldShowFullScreen={setShouldShowFullScreen}
          setIsFullScreenModeSafari={setIsFullScreenModeSafari}
          isFullScreenModeSafari={isFullScreenModeSafari}
        >
          {getContentWrapperChild()}
        </PollsContentManagerWrapper>
      }

      <ContainerModalManager isPollListLimitExceeded={isPollListLimitExceeded}/>

      {showMenuNavigation &&
        <PollsMobileNavigation
          pollsCount={copyPolls.length}
          activeTab={activeMobileTab}
          setActiveTab={setActiveTab}
          setShouldShowEllipsisMenu={setShouldShowEllipsisMenu}
        />
      }
    </div>
  );
};

export default observer(PollsContainer);
